'use client';

import { type KeyboardEvent, type RefObject, useCallback } from 'react';

import { KEYBOARD_SHIFT_STEP_SECONDS, KEYBOARD_STEP_SECONDS } from './constants';
import { type SelectionBounds } from './types';

/** Delay before re-focusing so the DOM has settled after the move. */
const REFOCUS_DELAY_MS = 10;

interface UseSelectionKeyboardReturn {
    handleKeyDown: (e: KeyboardEvent, selection: SelectionBounds) => void;
}

export function useSelectionKeyboard(
    videoDuration: number,
    onSelectionMove?: (startTime: number) => void,
    onSeek?: (time: number) => void,
    onPlayPause?: () => void,
    containerRef?: RefObject<HTMLDivElement | null>,
): UseSelectionKeyboardReturn {
    const handleKeyDown = useCallback(
        (e: KeyboardEvent, selection: SelectionBounds): void => {
            if (!onSelectionMove || videoDuration === 0) return;

            const currentStartTime = selection.startTime;
            let newStartTime = currentStartTime;
            let handled = true;

            switch (e.key) {
                case 'ArrowLeft': {
                    e.preventDefault();
                    const increment = e.shiftKey ? KEYBOARD_SHIFT_STEP_SECONDS : KEYBOARD_STEP_SECONDS;
                    newStartTime = Math.max(0, currentStartTime - increment);
                    break;
                }
                case 'ArrowRight': {
                    e.preventDefault();
                    const increment = e.shiftKey ? KEYBOARD_SHIFT_STEP_SECONDS : KEYBOARD_STEP_SECONDS;
                    newStartTime = Math.max(
                        0,
                        Math.min(videoDuration - selection.duration, currentStartTime + increment),
                    );
                    break;
                }
                case 'Home': {
                    e.preventDefault();
                    newStartTime = 0;
                    break;
                }
                case 'End': {
                    e.preventDefault();
                    newStartTime = Math.max(0, videoDuration - selection.duration);
                    break;
                }
                case 'Enter':
                case ' ': {
                    e.preventDefault();
                    onPlayPause?.();
                    return;
                }
                case 'Backspace':
                case 'Delete': {
                    e.preventDefault();
                    newStartTime = 0;
                    break;
                }
                default:
                    handled = false;
            }

            if (handled && newStartTime !== currentStartTime) {
                onSelectionMove(newStartTime);
                onSeek?.(newStartTime);

                requestAnimationFrame(() => {
                    setTimeout(() => {
                        const selectionHandle = containerRef?.current?.querySelector<HTMLElement>('.selection-handle');
                        if (selectionHandle && document.activeElement !== selectionHandle) {
                            selectionHandle.focus();
                        }
                    }, REFOCUS_DELAY_MS);
                });
            }
        },
        [videoDuration, onSelectionMove, onSeek, onPlayPause, containerRef],
    );

    return { handleKeyDown };
}
